import { useHistory } from "react-router-dom";
import { format as formatDate } from "date-fns";
import { ANYWHERE_LOCATION } from "@bookingcom/flights-searchbox";
import { trackMetaExperimentStage } from "../utils/meta-et-tracking.js";
import useCreateUrl from "./useCreateUrl.js";
import { useActions } from "../store/index.js";
import { useSelector, useDispatch } from "react-redux";
import { getSearch } from "../store/search/selectors.js";
import { getTravelPurpose } from "../store/travelPurpose/selectors.js";
import { actions as LoaderActions } from "../store/loadingScreen/actions.js";
import { actions as SearchResultsActions } from "../store/searchResults/actions.js";
import { actions as SearchResultsBoundsActions } from "../store/searchResultsBounds/actions.js";
import { actions as searchActions } from "../store/search/actions.js";
import { actions as flexibleDateMinPriceActions } from "../store/flexibleDateMinPrice/actions.js";
import useEventTracking from "./useEventTracking.js";
import useLocaleContext from "./useLocaleContext.js";
import useGlobalContext from "./useGlobalContext.js";
import { SEARCH_DATE_FORMAT } from "../store/search/state.js";
import useUserAgent from "./useUserAgent.js";
import { actions as TravelPurposeActions } from "../store/travelPurpose/actions.js";
import { useCallback, useEffect, useMemo } from "react";
import useRouteName from "./useRouteName.js";
import { isOfMetaOrigin } from "../utils/marketing-url-params.js";
import useFormatPrice from "./useFormatPrice.js";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { ANYWHERE, INFANT_MAX_AGE } from "@flights/constants";
import searchBoxSelectedDates from "../utils/search-box-selected-dates.js";
import { trackWithDefaultStage, customGoal, stage, goalWithValue } from "@flights/et-universal";
import { actions as searchBoxStateActions } from "../store/searchBoxState/actions.js";
import { cloneDeep } from "lodash";
import useFlyAnywhereC360Events from "./c360/useFlyAnywhereC360Events.js";
import { useGetFiltersDraft, useSetFiltersDraft } from "../components/elements/SearchResults/components/boundsFilterHooks.js";
import { useSplitBoundsCondition } from "../utils/experiments/funnel/flights_split_bounds_us.js";
import { devOverrideUtils, X_BOOKING_FLIGHTS_DEV_SERVICE_OVERRIDE } from "@flights/dev-overrides-universal";
import flights_web_cat_returning_user_prefill_search from "../utils/experiments/cat/flights_web_cat_returning_user_prefill_search.js";
import { convertSearchHistoryToSBState } from "../utils/convertSearchHistoryToSBState.js";
import { clearSplitBoundsBrandedFareParams } from "../utils/splitBoundsBrandedFareParams.js";
const CA_SOURCE_PAGES = {
    home: "flights_index_sb",
    "fly-anywhere": "flights_fly_anywhere_sb",
    searchresults: "flights_search_sb",
    "pb-change-flight": "flights_postbooking_sb",
    "not-found": "flights_404_sb"
};
const useSearchbox = () => {
    const history = useHistory();
    const { locale } = useLocaleContext();
    const { createUrl, enhanceURLSearchParams } = useCreateUrl();
    const loaderActions = useActions(LoaderActions);
    const actions = useActions(searchActions);
    const searchResultsActions = useActions(SearchResultsActions);
    const travelPurposeActions = useActions(TravelPurposeActions);
    const flexibleActions = useActions(flexibleDateMinPriceActions);
    const search = useSelector(getSearch);
    const travelPurpose = useSelector(getTravelPurpose);
    const { supplierFeatures, requestId, apiPrefix, ipCountry, searchHistoryPrefill, searchHistory } = useGlobalContext();
    const { isMobile } = useUserAgent();
    const trackV2 = useEventTracking("search_box", requestId);
    const routeName = useRouteName();
    const isMeta = isOfMetaOrigin();
    const { formatPrice } = useFormatPrice();
    const dispatch = useDispatch();
    const { isSplitBounds: shouldShowSplitBounds } = useSplitBoundsCondition();
    const prefillReturningUserSearch = !!searchHistoryPrefill;
    const trackReturningUserPrefillGoals = flights_web_cat_returning_user_prefill_search.useTrackReturningUserPrefillGoals();
    const trackFlyAnywhereC360 = useFlyAnywhereC360Events();
    const searchHandler = (callBackValues) => {
        let { fromAirport, toAirport, params, isDirect, searchState } = callBackValues;
        if (routeName === "home") {
            const indexPageLoadTime = flightsSessionStore.get("index_page_load_time");
            const timeToSearch = Math.floor((Date.now() - indexPageLoadTime) / 1000);
            flights_web_cat_returning_user_prefill_search.goalsWithValue.flights_cat_index_time_from_load_to_search_in_sec(timeToSearch);
        }
        if (fromAirport && fromAirport.includes("PNH.CITY")) {
            fromAirport = fromAirport.replace(/PNH\.CITY/g, "KTI.AIRPORT");
        }
        if (toAirport && toAirport.includes("PNH.CITY")) {
            toAirport = toAirport.replace(/PNH\.CITY/g, "KTI.AIRPORT");
        }
        const fromParam = params.get("from");
        if (fromParam && fromParam.includes("PNH.CITY")) {
            params.set("from", fromParam.replace(/PNH\.CITY/g, "KTI.AIRPORT"));
        }
        const toParam = params.get("to");
        if (toParam && toParam.includes("PNH.CITY")) {
            params.set("to", toParam.replace(/PNH\.CITY/g, "KTI.AIRPORT"));
        }
        const { sort } = search;
        params.set("sort", sort);
        clearSplitBoundsBrandedFareParams(params);
        const offerAccuracyTrackerId = searchState.searchSegments[0]?.offerAccuracyTrackerId;
        if (offerAccuracyTrackerId) {
            params.set("offerAccuracyTrackerId", offerAccuracyTrackerId);
        }
        params.set("travelPurpose", travelPurpose);
        const caSource = CA_SOURCE_PAGES[routeName];
        const caSourceParam = caSource ? `&ca_source=${caSource}` : "";
        const isCountry = toAirport.includes("COUNTRY");
        const isAnywhere = toAirport.includes(ANYWHERE);
        const isEmptyDestination = !toAirport;
        let newSearchURL = "";
        if (isAnywhere) {
            params.set("to", ANYWHERE);
            params.set("toCountryCode", ANYWHERE);
        }
        if (isCountry) {
            const toCountryCode = searchState.searchSegments[0].to[0].code;
            params.set("toCountryCode", toCountryCode);
            params.set("to", `${toCountryCode}.COUNTRY`);
        }
        const windowQuery = new URLSearchParams(window.location.search);
        const areAllParamsAlreadyPresentedInUrl = Array.from(params).every(([key, value]) => windowQuery.get(key) === value);
        if (areAllParamsAlreadyPresentedInUrl) {
            window.location.reload();
        }
        if (isAnywhere || isCountry || isEmptyDestination) {
            newSearchURL = `/fly-anywhere/?${params}${caSourceParam}`;
            const segment = searchState.searchSegments?.[0];
            const context = routeName === "searchresults" ? "search_results" : "fly_anywhere";
            trackFlyAnywhereC360("click_FA", {
                context,
                inbound_destination: segment?.from?.[0]?.name || "",
                outbound_destination: segment?.to?.[0]?.name || "Anywhere",
                inbound_dates: segment?.departureDate || "",
                outbound_dates: segment?.returnDate || "",
                journey_type: searchState.type || "ROUNDTRIP",
                cabin_class: searchState.cabinClass || "ECONOMY",
                direct: isDirect,
                number_of_travellers: {
                    adults: searchState.adults || 1,
                    children: searchState.children?.length || 0
                }
            });
        }
        else {
            newSearchURL = `/flights/${fromAirport}-${toAirport}/?${params}${caSourceParam}`;
            if (shouldShowSplitBounds) {
                newSearchURL = `/flights/${fromAirport}-${toAirport}/outbound/?${params}${caSourceParam}`;
            }
        }
        if (searchHistoryPrefill) {
            trackReturningUserPrefillGoals(searchHistoryPrefill, searchState);
        }
        actions.setValueFromSB(searchState);
        isDirect ? actions.resetFiltersButKeep({ stops: 0 }) : actions.resetFilters();
        searchResultsActions.reset();
        dispatch(SearchResultsBoundsActions.resetOutbound());
        dispatch(SearchResultsBoundsActions.resetInbound());
        flexibleActions.reset();
        loaderActions.show();
        if (!!apiPrefix) {
            window.location.assign(apiPrefix + createUrl(newSearchURL));
        }
        else {
            history.push(createUrl(newSearchURL));
        }
    };
    const filtersDraftWithBounds = useGetFiltersDraft();
    const setFiltersDraft = useSetFiltersDraft();
    const onDirectFlightsChangeHandler = (callbackParams) => {
        const { isDirect, params } = callbackParams;
        const stopsFilterFromSB = isDirect ? 0 : undefined;
        setFiltersDraft({
            ...filtersDraftWithBounds,
            stops: stopsFilterFromSB
        });
        enhanceURLSearchParams(params);
    };
    const onGetSBExperimentVariant = (name, defaultStageNo) => {
        return trackWithDefaultStage(name, defaultStageNo);
    };
    const onTrackSBExperiment = (callbackParams) => {
        const { name, stage: stageNo } = callbackParams;
        stage(name, stageNo);
    };
    const onTrackSBMetaExperimentStage = (callbackParams) => {
        const { name, stage } = callbackParams;
        trackMetaExperimentStage(name, stage);
    };
    const onTrackSBExperimentCustomGoal = (callbackParams) => {
        const { name, customGoal: goalNo } = callbackParams;
        customGoal(name, goalNo);
    };
    const onTrackSBExperimentGoalWithValue = (callbackParams) => {
        const { name, value } = callbackParams;
        goalWithValue(name, value);
    };
    const onTrackSBExperimentStage = (callbackParams) => {
        const { name, stage: stageNo } = callbackParams;
        stage(name, stageNo);
    };
    const stops = filtersDraftWithBounds.stops;
    const lastSearchState = useMemo(() => {
        if (prefillReturningUserSearch && routeName === "home" && searchHistoryPrefill) {
            return searchHistoryPrefill;
        }
        return undefined;
    }, [prefillReturningUserSearch, routeName, searchHistoryPrefill]);
    useEffect(() => {
        if (prefillReturningUserSearch && lastSearchState) {
            actions.setValueFromSB(lastSearchState);
        }
    }, [prefillReturningUserSearch, lastSearchState]);
    const { searchSegments } = search;
    const initialSearchSegments = cloneDeep(searchSegments);
    if (initialSearchSegments && initialSearchSegments[0]) {
        const currSegment = initialSearchSegments[0];
        if (currSegment.departureDate === undefined && currSegment.returnDate === undefined) {
            const { startDate, endDate } = searchBoxSelectedDates();
            if (startDate)
                initialSearchSegments[0].departureDate = formatDate(startDate, SEARCH_DATE_FORMAT);
            if (endDate)
                initialSearchSegments[0].returnDate = formatDate(endDate, SEARCH_DATE_FORMAT);
        }
        if (routeName === "fly-anywhere" && currSegment.to.length === 0) {
            currSegment.to[0] = ANYWHERE_LOCATION;
        }
    }
    const searchboxInitValue = useMemo(() => {
        if (prefillReturningUserSearch && lastSearchState) {
            return lastSearchState;
        }
        return {
            type: search.type,
            searchSegments: initialSearchSegments,
            adults: search.adults,
            children: search.children,
            cabinClass: search.cabinClass,
            isDirect: search.isDirect
        };
    }, [
        prefillReturningUserSearch,
        lastSearchState,
        search.type,
        search.adults,
        search.children,
        search.cabinClass,
        search.isDirect,
        initialSearchSegments
    ]);
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlTravelPurpose = (urlParams.get("travelPurpose") || "leisure");
        if (urlTravelPurpose === travelPurpose)
            return;
        setTimeout(() => travelPurposeActions.setTravelPurpose(urlTravelPurpose), 0);
    }, [travelPurposeActions, travelPurpose]);
    const onTravelPurposeChange = (value) => {
        const urlParams = new URLSearchParams(location.search);
        urlParams.set("travelPurpose", value);
        window.history.replaceState(null, "", `${location.pathname}?${urlParams.toString()}`);
        travelPurposeActions.setTravelPurpose(value);
    };
    const multiSelectVariant = useMemo(() => {
        if (isMobile) {
            return 2;
        }
        return 3;
    }, [isMobile]);
    const onChange = useCallback((params) => {
        dispatch(searchBoxStateActions.set(params));
    }, [dispatch]);
    const getOptionalHeaders = () => {
        const devOverrides = devOverrideUtils.getAll();
        if (typeof devOverrides === "object" && Object.keys(devOverrides).length > 0) {
            return {
                [X_BOOKING_FLIGHTS_DEV_SERVICE_OVERRIDE]: JSON.stringify(devOverrides)
            };
        }
        return undefined;
    };
    const handleRecentSearchSelect = useCallback((search) => {
        const sbState = convertSearchHistoryToSBState(search);
        onChange(sbState);
    }, [onChange]);
    return {
        onSearch: searchHandler,
        onChange,
        onDirectFlightsChange: onDirectFlightsChangeHandler,
        initialValue: searchboxInitValue,
        directFlightsFilterApplied: prefillReturningUserSearch && lastSearchState ? lastSearchState.isDirect : stops === 0,
        track: () => { },
        trackV2,
        onGetSBExperimentVariant,
        onTrackSBExperiment,
        onTrackSBExperimentCustomGoal,
        onTrackSBExperimentGoalWithValue,
        onTrackSBExperimentStage,
        onTrackSBMetaExperimentStage,
        dateFNSLocale: locale,
        isMultiStopEnabled: supplierFeatures?.includes("SHOW_MULTI_STOP") || false,
        isSearchResults: routeName.includes("searchresults"),
        infantMaxAge: INFANT_MAX_AGE,
        priceFormatter: formatPrice,
        searchAutoCompleteBaseURL: !!apiPrefix ? { url: apiPrefix, accessToken: "" } : undefined,
        getOptionalHeaders,
        isA11yEnabled: false,
        travelPurpose: travelPurpose,
        onTravelPurposeChange: onTravelPurposeChange,
        isShowTravelPurposeCheckbox: false,
        trackLandingGoal: routeName === "home",
        isMobile,
        isMeta,
        ipCountry,
        multiSelectVariant,
        routeName,
        config: {
            hideMultiCityEmptyInputs: routeName.includes("searchresults")
        },
        fixInputSpacing: false,
        recentSearchConfig: {
            enabled: true,
            searchHistory,
            onSelect: handleRecentSearchSelect
        }
    };
};
export default useSearchbox;
